import { useEffect, useRef, useState } from 'react';
import FocusTrap from 'focus-trap-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import type { Definition } from '../content/definitions';

export function DefinitionPopover({ definition }: { definition: Definition }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-label={`Definition for ${definition.title}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className="rounded-full border border-slate-300 px-1.5 py-0.5 text-[10px] leading-none text-slate-600 hover:bg-slate-100"
      >
        ?
      </button>
      {open && (
        <FocusTrap focusTrapOptions={{ initialFocus: false, returnFocusOnDeactivate: true, escapeDeactivates: false, tabbableOptions: { displayCheck: 'none' } }}>
          <div
            className="absolute left-0 top-6 z-20 w-72 rounded border border-slate-200 bg-white p-3 text-xs shadow-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 text-sm font-semibold text-slate-900">{definition.title}</div>
            <div className="mb-2 italic text-slate-500">{definition.shortHelp}</div>
            <div className="prose prose-sm max-w-none">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{definition.body}</ReactMarkdown>
            </div>
            <Link
              to={`/help#${definition.id}`}
              onClick={() => setOpen(false)}
              className="mt-2 inline-block text-brand-dark hover:underline"
            >
              See full definitions →
            </Link>
          </div>
        </FocusTrap>
      )}
    </div>
  );
}
